import Logger from '../../../services/Logger';
import UiBlocker from '../../../services/UiBlocker';
import localization from '../../../shared/strings';
import * as utils from '../../../shared/utils';
import TYPES from '../../action-types';
import { showDialog } from '../../../components/dialog';
import Toast from '../../../components/Toast';
import sdk from '../../../sdk';
import { navigate, reloadCurrentPage } from '../../../helpers/history';
import buildCopyConvertingParams from '../../../helpers/assets/buildCopyConvertingParams';
import { isAssetConvertibleForBulkCopy } from '../../../helpers/assets/computeBulkCopySelectionFlags';

import getThumbnails from './getThumbnails';

/**
 * Copy / duplicate assets into a collection, optionally with rendition (convertingParams).
 * @param {string[]} ids - original asset ids
 * @param {string} targetCollectionId - collection id where new assets will be created
 * @param {boolean} resolveDuplicates
 * @param {boolean} overwriteDuplicates
 * @param {object} [convertionParams] - dialog output (mimeType, quality, …). Omitted or mimeType "original" => plain copy.
 */
const duplicateAssets = (
  ids, targetCollectionId, resolveDuplicates, overwriteDuplicates, convertionParams,
) => async (dispatch, getAll) => {
  const store = getAll();
  const isUserOnGd = store.user.team.storageType === 'gd';

  const copiedToActiveCollection = store.collections.activeCollections.findIndex((activeCollection) => activeCollection._id === targetCollectionId) > -1;

  const convertingParams = buildCopyConvertingParams(convertionParams);

  // When converting, the server skips non-convertible assets (e.g. `.obj`), so
  // don't put the optimistic "copying" badge on them — otherwise it never clears
  // (no background event for skipped assets) and lingers until a page reload.
  // Only badge assets we can positively confirm are convertible: an asset missing
  // from the catalog can't be confirmed (and can't be badged by the reducer
  // anyway), so it must not be assumed convertible.
  const allIds = ids || [];
  const copyingAssetIds = convertingParams
    ? allIds.filter((id) => {
      const asset = store.assets.items.find((item) => item._id === id);
      return asset ? isAssetConvertibleForBulkCopy(asset) : false;
    })
    : allIds;

  try {
    dispatch({
      type: TYPES.ASSETS.COPY_ASSETS.START,
      payload: { assetIds: allIds, copyingAssetIds },
    });
    UiBlocker.block();

    const { data: result } = await sdk.assets.copy(
      ids,
      targetCollectionId,
      resolveDuplicates,
      overwriteDuplicates,
      convertingParams ?? undefined,
    );

    const { queued = false, copiedAssets = [] } = result;

    dispatch({
      type: TYPES.ASSETS.COPY_ASSETS.COMPLETE,
      payload: {
        copiedAssets,
        isActiveCollection: copiedToActiveCollection,
        isQueued: Boolean(queued),
      },
    });

    const copiedAssetsIds = copiedAssets.map((asset) => asset._id);
    if (copiedAssetsIds.length && !queued) getThumbnails(copiedAssetsIds)(dispatch, getAll);

    if (queued) {
      Toast(localization.DETAILS.textFinishDuplicationInBg, { autoClose: false });
    }
    UiBlocker.unblock();
  } catch (error) {
    dispatch({
      type: TYPES.ASSETS.COPY_ASSETS.FAILED,
      payload: { ids, markCopyFailedIds: ids },
      error,
    });
    const errorStatus = utils.getStatusFromResponceError(error);
    const subcode = utils.getDataFromResponceError(error, 'subcode');
    const { ERROR_COPY_ASSET } = localization.DIALOGS;
    Logger.log('ErrorStatus', errorStatus);

    if (subcode === 'MissingCanCopyGoogleDriveCapabilityError'
      || subcode === 'MissingCanAddChildrenGoogleDriveCapabilityError'
      || subcode === 'StorageSpaceLimitExceededError') {
      const hideDialog = showDialog({
        title: ERROR_COPY_ASSET.TITLE(!isUserOnGd ? 'Picsio' : ''),
        children: ERROR_COPY_ASSET.TEXT(!isUserOnGd ? 'Picsio' : ''),
        textBtnCancel: null,
        textBtnOk: 'Check storage space',
        onOk() {
          Logger.log('User', 'Copy failed, not enough storage');
          navigate('/storage');
          hideDialog();
        },
      });
    } else if (subcode === 'AssetsLimitExceededError') {
      Toast(localization.DETAILS.textAssetsLimitExceededError, { autoClose: false, type: 'error' });
    } else if (subcode === 'CopyAssetsLimitError') {
      Toast(localization.DETAILS.textCopyAssetsLimitError, { autoClose: false, type: 'error' });
    } else if (subcode === 'RestrictedAssetsError') {
      Toast(localization.DETAILS.textRestrictedAssetsError, { autoClose: false });
    } else {
      Toast(localization.DETAILS.textAssetDuplicateFail, {
        autoClose: false, type: 'error', onOk: reloadCurrentPage, btnOkValue: localization.HISTORY.textBtnRefresh,
      });
    }
    Logger.error(new Error('Can not duplicate asset'), { error }, [
      'RestrictAssetsFailed',
      { errorMessage: (error && error.message) || 'NoMessage' },
    ]);
    UiBlocker.unblock();
  }
};

export default duplicateAssets;
